import { parse } from 'graphql';

import type { RuntimeEnvironment } from '../graphql/config/runtimeConfig';
import type {
  BrokerageCanvassersQuery,
  BrokerageCanvassersQueryVariables,
} from '../graphql/queries/brokerageCanvassers.generated';
import { createAuthenticatedGraphQLClient } from './createAuthenticatedGraphQLClient';

export type CanvasserDirectory = ReadonlyMap<string, string>;

export type BrokerageCanvasserDirectoryRequestOptions = {
  signal?: AbortSignal;
};

export type BrokerageCanvasserDirectoryApi = {
  loadCanvasserDirectory(brokerageId: string, options?: BrokerageCanvasserDirectoryRequestOptions): Promise<CanvasserDirectory>;
};

type AuthenticatedClient = ReturnType<typeof createAuthenticatedGraphQLClient>;
type AuthenticatedClientFactory = (options: Parameters<typeof createAuthenticatedGraphQLClient>[0]) => AuthenticatedClient;

export type CreateBrokerageCanvasserDirectoryApiOptions = {
  env: RuntimeEnvironment;
  fetchImpl?: Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];
  authenticatedClientFactory?: AuthenticatedClientFactory;
};

// Keep this selection set in sync with brokerageCanvassers.graphql. Codegen emits
// operation types only, so this is the runtime document sent to Core.
export const BROKERAGE_CANVASSERS_QUERY = parse(/* GraphQL */ `
  query BrokerageCanvassers($brokerageId: ID!) @api(name: core) {
    brokerage(id: $brokerageId) {
      agents {
        id
        name
      }
    }
  }
`);

export class BrokerageCanvasserDirectoryLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BrokerageCanvasserDirectoryLoadError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nonBlankTrimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  return trimmed || null;
};

const readAgents = (result: unknown): unknown[] => {
  if (!isRecord(result) || !Object.hasOwn(result, 'brokerage')) {
    throw new BrokerageCanvasserDirectoryLoadError('Core did not return a brokerage directory response.');
  }

  const brokerage = result.brokerage;
  if (!isRecord(brokerage) || !Object.hasOwn(brokerage, 'agents')) {
    throw new BrokerageCanvasserDirectoryLoadError('Core did not return a brokerage directory.');
  }

  const agents = brokerage.agents;
  if (agents === null) return [];
  if (!Array.isArray(agents)) {
    throw new BrokerageCanvasserDirectoryLoadError('Core returned an invalid brokerage agent directory.');
  }

  return agents;
};

export const normalizeBrokerageCanvasserDirectory = (result: unknown): CanvasserDirectory => {
  const directory = new Map<string, string>();

  for (const agent of readAgents(result)) {
    if (!isRecord(agent) || !Object.hasOwn(agent, 'id')) continue;

    const id = nonBlankTrimmedString(agent.id);
    if (id === null || !Object.hasOwn(agent, 'name')) continue;

    const name = nonBlankTrimmedString(agent.name);
    if (name !== null) directory.set(id, name);
  }

  return directory;
};

export const createBrokerageCanvasserDirectoryApi = ({
  env,
  fetchImpl,
  authenticatedClientFactory = createAuthenticatedGraphQLClient,
}: CreateBrokerageCanvasserDirectoryApiOptions): BrokerageCanvasserDirectoryApi => {
  const client = authenticatedClientFactory({ env, fetchImpl });

  return {
    loadCanvasserDirectory: async (brokerageId, options = {}) => {
      const variables: BrokerageCanvassersQueryVariables = { brokerageId };
      const result = await client.request<BrokerageCanvassersQuery>({
        document: BROKERAGE_CANVASSERS_QUERY,
        variables,
        operationName: 'BrokerageCanvassers',
        signal: options.signal,
      });

      return normalizeBrokerageCanvasserDirectory(result);
    },
  };
};
